import { createReactiveLocalStorage } from '~/utils/storage';

import { type AppViewConfig, makeCustomAppViewId, upsertCustomAppView } from './appview-utils';

export { isCustomAppView, makeCustomAppViewId, upsertCustomAppView } from './appview-utils';
export type { AppViewConfig } from './appview-utils';

/**
 * An AppView is the service that aggregates and serves Bluesky-style views
 * (timelines, profiles, notifications, search, …) on top of the AT Protocol
 * network. The official Bluesky AppView is the default, but the network is
 * provider-neutral: alternative AppViews such as Blacksky index the same
 * firehose and can be used interchangeably.
 *
 * Selecting an AppView affects two things:
 *
 *   1. Unauthenticated/public requests, which are sent directly to {@link AppViewConfig.url}.
 *   2. Authenticated requests, which are sent to the user's PDS together with
 *      an `atproto-proxy: <did>` header instructing the PDS to forward them to
 *      this AppView's service.
 */

export const BSKY_APPVIEW: AppViewConfig = {
	id: 'bsky',
	name: 'Bluesky',
	url: 'https://api.bsky.app',
	did: 'did:web:api.bsky.app#bsky_appview',
};

export const BLACKSKY_APPVIEW: AppViewConfig = {
	id: 'blacksky',
	name: 'Blacksky',
	url: 'https://api.blacksky.community',
	did: 'did:web:api.blacksky.community#bsky_appview',
};

/** Built-in AppViews offered out of the box. */
export const APPVIEW_PRESETS: readonly AppViewConfig[] = [BSKY_APPVIEW, BLACKSKY_APPVIEW];

/** AppView used when the user hasn't picked one yet. */
export const DEFAULT_APPVIEW = BSKY_APPVIEW;

interface AppViewStore {
	$version: 1;
	/** The currently selected AppView. */
	current: AppViewConfig;
	/** User-defined custom AppViews, persisted so they remain selectable. */
	customs: AppViewConfig[];
}

const STORE_KEY = 'rantai_appview';

/**
 * Reactive, persisted store for the AppView selection. Lives in the API layer
 * (rather than the desktop preferences) so both the networking globals and any
 * UI surface can read it without crossing layering boundaries.
 */
export const appviewStore = createReactiveLocalStorage<AppViewStore>(STORE_KEY, (version) => {
	if (version === 0) {
		const obj: AppViewStore = {
			$version: 1,
			current: DEFAULT_APPVIEW,
			customs: [],
		};

		return obj;
	}

	throw new Error(`unhandled appview store migration: ${version}`);
});

/** All AppViews available to pick from: built-in presets followed by saved customs. */
export const getAvailableAppViews = (): AppViewConfig[] => {
	return [...APPVIEW_PRESETS, ...appviewStore.customs];
};

/** The currently selected AppView. Reactive when read inside a tracking scope. */
export const getCurrentAppView = (): AppViewConfig => {
	return appviewStore.current;
};

/** Switch the active AppView. */
export const setCurrentAppView = (config: AppViewConfig): void => {
	appviewStore.current = config;
};

/**
 * Save (or update) a custom AppView and make it the active selection. Custom
 * entries are keyed by their service URL.
 */
export const saveCustomAppView = (config: Omit<AppViewConfig, 'id'>): AppViewConfig => {
	const entry: AppViewConfig = { ...config, id: makeCustomAppViewId(config.url) };

	appviewStore.customs = upsertCustomAppView(appviewStore.customs, entry);
	appviewStore.current = entry;
	return entry;
};

/** Remove a saved custom AppView, falling back to the default if it was active. */
export const removeCustomAppView = (id: string): void => {
	const index = appviewStore.customs.findIndex((c) => c.id === id);
	if (index !== -1) {
		appviewStore.customs.splice(index, 1);
	}

	if (appviewStore.current.id === id) {
		appviewStore.current = DEFAULT_APPVIEW;
	}
};
